import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Ticket, DollarSign, TrendingUp, ScanLine, ChevronRight, Loader, ExternalLink } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import PremiumFeatureGate from './PremiumFeatureGate';
import { getUserEvents } from '../../firebaseFunctions/getUserEvents/getUserEvents';
import { getTicketTypes, getTicketSales } from '../../services/TicketingService';

const formatCurrency = (n) => `$${Number(n || 0).toFixed(2)}`;

const TicketingDashboard = () => {
  const { t } = useTranslation();
  const { user, subscriptionTier } = useAuth();
  const isPro = subscriptionTier === 'pro' || subscriptionTier === 'premium' || subscriptionTier === 'enterprise';

  const [events, setEvents] = useState([]);
  const [eventTickets, setEventTickets] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user?.uid && isPro) loadData();
  }, [user?.uid, isPro]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    setIsLoading(true);
    const eventsResult = await getUserEvents(user.uid);
    const nonDraft = (Array.isArray(eventsResult) ? eventsResult : []).filter(e => !e.isDraft);
    setEvents(nonDraft);

    const ticketData = await Promise.all(
      nonDraft.map(async (e) => {
        const typesResult = await getTicketTypes(e.id);
        const types = typesResult.success ? typesResult.ticketTypes : [];
        const hasTickets = types.length > 0;

        let totals = { sold: 0, gross: 0, net: 0 };
        if (hasTickets) {
          const salesResult = await getTicketSales(e.id, user.uid);
          if (salesResult.success) totals = salesResult.totals;
        }

        return { eventId: e.id, types, totals, hasTickets };
      })
    );

    const ticketMap = {};
    ticketData.forEach(d => { ticketMap[d.eventId] = d; });
    setEventTickets(ticketMap);
    setIsLoading(false);
  };

  const now = new Date();

  const getEventStatus = (e) => {
    const start = new Date(`${e.startDate}T${e.startTime || '00:00'}`);
    const end = new Date(`${e.endDate}T${e.endTime || '23:59'}`);
    if (start <= now && end >= now) return 'ongoing';
    if (start > now) return 'upcoming';
    return 'past';
  };

  const aggregateTotals = Object.values(eventTickets).reduce((acc, d) => {
    acc.sold += d.totals.sold || 0;
    acc.gross += d.totals.gross || 0;
    acc.net += d.totals.net || 0;
    return acc;
  }, { sold: 0, gross: 0, net: 0 });

  const content = (
    <div className="p-6 sm:p-8">
      <div className="max-w-4xl">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-grey-900">{t('ticketing.title')}</h1>
          <p className="text-sm text-grey-500 mt-1">{t('ticketing.subtitle')}</p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader className="w-6 h-6 text-grey-400 animate-spin" />
          </div>
        ) : (
          <>
            {/* Aggregate stats */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="bg-white border border-grey-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Ticket className="w-4 h-4 text-grey-400" />
                  <span className="text-xs text-grey-500">{t('ticketing.totalSold')}</span>
                </div>
                <p className="text-2xl font-semibold text-grey-900">{aggregateTotals.sold}</p>
                <p className="text-xs text-grey-400 mt-0.5">{t('ticketing.acrossAllEvents')}</p>
              </div>
              <div className="bg-white border border-grey-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-4 h-4 text-grey-400" />
                  <span className="text-xs text-grey-500">{t('ticketing.grossRevenue')}</span>
                </div>
                <p className="text-2xl font-semibold text-grey-900">{formatCurrency(aggregateTotals.gross)}</p>
              </div>
              <div className="bg-white border border-grey-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-grey-400" />
                  <span className="text-xs text-grey-500">{t('ticketing.yourNet')}</span>
                </div>
                <p className="text-2xl font-semibold text-grey-900">{formatCurrency(aggregateTotals.net)}</p>
              </div>
            </div>

            {/* Ongoing events - check-in ready */}
            {events.some(e => getEventStatus(e) === 'ongoing') && (
              <div className="mb-6">
                <h2 className="text-xs font-semibold text-grey-500 uppercase tracking-wide mb-2">{t('ticketing.happeningNow')}</h2>
                <div className="space-y-2">
                  {events.filter(e => getEventStatus(e) === 'ongoing').map(e => {
                    const td = eventTickets[e.id];
                    return (
                      <div key={e.id} className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-4">
                        <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-grey-900 truncate">{e.eventName}</p>
                          <p className="text-xs text-grey-500 mt-0.5">
                            {td?.hasTickets
                              ? `${td.totals.sold} checked in · ${formatCurrency(td.totals.gross)} ${t('ticketing.gross')}`
                              : t('ticketing.noTicketsConfigured')}
                          </p>
                        </div>
                        <Link
                          to={`/eo/events/${e.id}/checkin`}
                          className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition font-medium flex-shrink-0"
                        >
                          <ScanLine className="w-4 h-4" />
                          {t('ticketing.checkIn')}
                        </Link>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* All events */}
            <h2 className="text-xs font-semibold text-grey-500 uppercase tracking-wide mb-2">{t('ticketing.allEvents')}</h2>
            {events.length === 0 ? (
              <div className="bg-grey-50 border border-dashed border-grey-300 rounded-xl p-12 text-center">
                <Ticket className="w-10 h-10 text-grey-300 mx-auto mb-3" />
                <p className="font-medium text-grey-700 mb-1">{t('ticketing.noEventsYet')}</p>
                <p className="text-sm text-grey-500">{t('ticketing.noEventsDesc')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {events.map(e => {
                  const td = eventTickets[e.id];
                  const status = getEventStatus(e);
                  return (
                    <div key={e.id} className="bg-white border border-grey-200 rounded-xl p-4 flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-grey-900 truncate">{e.eventName}</p>
                          {status === 'ongoing' && (
                            <span className="flex-shrink-0 px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] font-medium rounded-full">Live</span>
                          )}
                          {status === 'upcoming' && (
                            <span className="flex-shrink-0 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-medium rounded-full">Upcoming</span>
                          )}
                        </div>
                        {td?.hasTickets ? (
                          <p className="text-xs text-grey-500 mt-0.5">
                            {td.totals.sold} {t('ticketing.sold')} · {formatCurrency(td.totals.gross)} {t('ticketing.gross')} · {formatCurrency(td.totals.net)} {t('ticketing.net')}
                          </p>
                        ) : (
                          <p className="text-xs text-grey-400 mt-0.5">{t('ticketing.noTicketTypesSetUp')}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {td?.hasTickets && (
                          <a
                            href={`${window.location.origin}/events/${e.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 px-3 py-1.5 border border-grey-200 text-grey-500 text-xs rounded-lg hover:bg-grey-50 transition"
                            title="Public ticket page"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                        {status === 'ongoing' && (
                          <Link
                            to={`/eo/events/${e.id}/checkin`}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition font-medium"
                          >
                            <ScanLine className="w-3.5 h-3.5" />
                            {t('ticketing.checkIn')}
                          </Link>
                        )}
                        <Link
                          to={`/eo/events/${e.id}`}
                          className="flex items-center gap-1 px-3 py-1.5 border border-grey-300 text-grey-700 text-xs rounded-lg hover:bg-grey-50 transition font-medium"
                        >
                          {t('ticketing.manage')}
                          <ChevronRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  return (
    <PremiumFeatureGate featureName="Ticketing">
      {content}
    </PremiumFeatureGate>
  );
};

export default TicketingDashboard;
